import {
  Directive,
  ElementRef,
  HostListener,
  inject,
  Injector,
  Input,
  OnDestroy,
  PLATFORM_ID,
  signal,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Overlay, OverlayRef, PositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { TOOLTIP_DATA, TooltipContainerComponent } from '../../components/tooltip-container/tooltip-container.component';
import { fromEvent, Subscription } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { TooltipControlService } from '../../services/tooltip-control.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Directive({
  selector: '[workUiTooltip]',
  standalone: true,
})
export class TooltipDirective implements OnDestroy {
  @Input() workUiTooltip: string | TemplateRef<void> | undefined;
  @Input() workUiTooltipMaxWidth: number | undefined;
  @Input() workUiTooltipOpenOnClick = false;
  /** Opt-in: open the tooltip on click on mobile/tablet (default: disabled there). Independent of `workUiTooltipOpenOnClick`, which governs desktop. */
  @Input() workUiTooltipOpenOnClickOnTouch = false;
  private element = inject<ElementRef<HTMLElement>>(ElementRef);
  private overlay = inject(Overlay);
  private viewContainer = inject(ViewContainerRef);
  private tooltipControlService = inject(TooltipControlService);
  private platformId = inject(PLATFORM_ID);
  private breakpointObserver = inject(BreakpointObserver);
  private overlayRef: OverlayRef | null = null;
  private outsideClickSubscription: Subscription | undefined;
  private tooltipAttached = false;
  private tooltipDisabled = false;
  private isMobileOrTablet = signal(false);
  // Stable bound reference shared by addEventListener and removeEventListener — `.bind(this)`
  // produces a new function on every call, so the listener would never be removed and would
  // accumulate on `window` for the lifetime of the page (one leaked listener per tooltip attach).
  private readonly hideTooltipOnWindowScroll = (): void => this.hideTooltip();

  constructor() {
    this.tooltipControlService.tooltipsDisabled.pipe(takeUntilDestroyed()).subscribe((disabled) => (this.tooltipDisabled = disabled));
    this.breakpointObserver
      .observe([Breakpoints.Handset, Breakpoints.Tablet])
      .pipe(takeUntilDestroyed())
      .subscribe((result) => {
        this.isMobileOrTablet.set(result.matches);
      });
  }

  @HostListener('mouseenter')
  @HostListener('focus')
  showTooltip(): void {
    if (
      this.workUiTooltip === '' ||
      this.overlayRef?.hasAttached() ||
      this.workUiTooltipOpenOnClick ||
      this.tooltipDisabled ||
      this.isMobileOrTablet()
    ) {
      return;
    }
    this.attachTooltip();
  }

  @HostListener('mouseleave')
  @HostListener('blur')
  hideTooltipOnMouseLeave(): void {
    if (this.isMobileOrTablet()) {
      return;
    }
    if (!this.workUiTooltipOpenOnClick && this.tooltipAttached) {
      this.hideTooltip();
    }
  }

  @HostListener('click')
  toggleTooltipOnClick(): void {
    const clickEnabled = this.isMobileOrTablet() ? this.workUiTooltipOpenOnClickOnTouch : this.workUiTooltipOpenOnClick;
    if (!clickEnabled) {
      return;
    }
    if (this.overlayRef?.hasAttached()) {
      this.hideTooltip();
    } else {
      this.attachTooltip();
      this.listenForOutsideClicks();
    }
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
    this.outsideClickSubscription?.unsubscribe();
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('scroll', this.hideTooltipOnWindowScroll, true);
    }
  }

  private attachTooltip(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('scroll', this.hideTooltipOnWindowScroll, true);
    }

    if (this.overlayRef === null) {
      const positionStrategy = this.getPositionStrategy();
      this.overlayRef = this.overlay.create({ positionStrategy });
    }

    const injector = Injector.create({
      providers: [
        {
          provide: TOOLTIP_DATA,
          useValue: this.workUiTooltip,
        },
      ],
    });
    const component = new ComponentPortal(TooltipContainerComponent, this.viewContainer, injector);
    const componentRef = this.overlayRef.attach(component);

    if (this.workUiTooltipMaxWidth) {
      componentRef.instance.maxWidth = this.workUiTooltipMaxWidth;
    }
    this.tooltipAttached = true;
  }

  private getPositionStrategy(): PositionStrategy {
    return this.overlay
      .position()
      .flexibleConnectedTo(this.element)
      .withPositions([
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          panelClass: 'top',
        },
        {
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top',
          panelClass: 'bottom',
        },
      ]);
  }

  private hideTooltip(): void {
    if (this.overlayRef?.hasAttached()) {
      this.overlayRef?.detach();
      this.removeOutsideClickListener();
      if (isPlatformBrowser(this.platformId)) {
        window.removeEventListener('scroll', this.hideTooltipOnWindowScroll, true);
      }
    }
    this.tooltipAttached = false;
  }

  private listenForOutsideClicks(): void {
    this.outsideClickSubscription?.unsubscribe();
    this.outsideClickSubscription = fromEvent<MouseEvent>(document, 'click').subscribe((event) => {
      const clickTarget = event.target as HTMLElement;
      if (this.overlayRef && !this.element.nativeElement.contains(clickTarget) && this.overlayRef.hasAttached()) {
        this.hideTooltip();
      }
    });
  }

  private removeOutsideClickListener(): void {
    this.outsideClickSubscription?.unsubscribe();
    this.outsideClickSubscription = undefined;
  }
}
